import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal, { ModalFooter } from '@/components/common/Modal';
import Badge from '@/components/common/Badge';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  listApplications,
  listPartners,
  reviewApplication,
  togglePartner,
  getDashboardStats,
} from '@/services/affiliateApi';
import type {
  AffiliateApplication,
  AffiliatePartner,
  AffiliateDashboardStats,
} from '@/types/affiliate';

type TabKey = 'stats' | 'applications' | 'partners';

export default function AffiliateManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('stats');
  const [appPage, setAppPage] = useState(1);
  const [appStatusFilter, setAppStatusFilter] = useState('all');
  const [partnerPage, setPartnerPage] = useState(1);
  const [reviewModal, setReviewModal] = useState<AffiliateApplication | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Queries
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['affiliate-stats'],
    queryFn: getDashboardStats,
  });

  const { data: appsData, isLoading: appsLoading } = useQuery({
    queryKey: ['affiliate-applications', appPage, appStatusFilter],
    queryFn: () => listApplications(appPage, 20, appStatusFilter !== 'all' ? appStatusFilter : undefined),
    enabled: activeTab === 'applications' || activeTab === 'stats',
  });

  const { data: partnersData, isLoading: partnersLoading } = useQuery({
    queryKey: ['affiliate-partners', partnerPage],
    queryFn: () => listPartners(partnerPage, 20),
    enabled: activeTab === 'partners' || activeTab === 'stats',
  });

  // Mutations
  const approveMutation = useMutation({
    mutationFn: (appId: string) => reviewApplication(appId, 'approved'),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-applications'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-partners'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-stats'] });
      toast.success(`Application approved! Code: ${data.promo_code}`);
      setReviewModal(null);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Approval failed'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ appId, reason }: { appId: string; reason: string }) =>
      reviewApplication(appId, 'rejected', reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-applications'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-stats'] });
      toast.success('Application rejected');
      setReviewModal(null);
      setRejectionReason('');
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Rejection failed'),
  });

  const toggleMutation = useMutation({
    mutationFn: (partnerId: string) => togglePartner(partnerId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-partners'] });
      toast.success(data.message);
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : 'Toggle failed'),
  });

  const tabs: { key: TabKey; label: string; badge?: number }[] = [
    { key: 'stats', label: 'Program Stats' },
    { key: 'applications', label: 'Applications', badge: stats?.applications.pending },
    { key: 'partners', label: 'Partners' },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Affiliate Program</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.key
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.badge ? (
                <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {tab.badge}
                </span>
              ) : null}
            </button>
          ))}
        </nav>
      </div>

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <StatsView stats={stats} loading={statsLoading} />
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <ApplicationsView
          data={appsData}
          loading={appsLoading}
          page={appPage}
          setPage={setAppPage}
          statusFilter={appStatusFilter}
          setStatusFilter={setAppStatusFilter}
          onReview={setReviewModal}
        />
      )}

      {/* Partners Tab */}
      {activeTab === 'partners' && (
        <PartnersView
          data={partnersData}
          loading={partnersLoading}
          page={partnerPage}
          setPage={setPartnerPage}
          onToggle={(id) => toggleMutation.mutate(id)}
          onViewDetail={(id) => navigate(`/affiliates/${id}`)}
        />
      )}

      {/* Review Modal */}
      {reviewModal && (
        <Modal title="Review Application" onClose={() => { setReviewModal(null); setRejectionReason(''); }}>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="font-medium text-gray-500">Name:</span> {reviewModal.full_name}</div>
              <div><span className="font-medium text-gray-500">Email:</span> {reviewModal.email}</div>
              <div><span className="font-medium text-gray-500">Phone:</span> {reviewModal.phone || 'N/A'}</div>
              <div><span className="font-medium text-gray-500">Organization:</span> {reviewModal.organization || 'N/A'}</div>
              <div><span className="font-medium text-gray-500">Type:</span> {reviewModal.organization_type || 'N/A'}</div>
              <div><span className="font-medium text-gray-500">Website:</span> {reviewModal.website_url || 'N/A'}</div>
            </div>
            {reviewModal.motivation && (
              <div>
                <span className="font-medium text-gray-500 text-sm">Motivation:</span>
                <p className="mt-1 text-sm text-gray-700">{reviewModal.motivation}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rejection Reason (if rejecting)
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm"
                rows={3}
                placeholder="Optional reason for rejection..."
              />
            </div>
          </div>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => { setReviewModal(null); setRejectionReason(''); }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => rejectMutation.mutate({ appId: reviewModal.id, reason: rejectionReason })}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? 'Rejecting...' : 'Reject'}
            </Button>
            <Button
              onClick={() => approveMutation.mutate(reviewModal.id)}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? 'Approving...' : 'Approve'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

// ── Stats View ──
function StatsView({ stats, loading }: { stats?: AffiliateDashboardStats; loading: boolean }) {
  if (loading) return <div className="text-gray-500">Loading stats...</div>;
  if (!stats) return null;

  const cards = [
    { label: 'Pending Applications', value: stats.applications.pending, color: 'text-yellow-600' },
    { label: 'Active Affiliates', value: stats.affiliates.active, color: 'text-green-600' },
    { label: 'Total Affiliate Referrals', value: stats.affiliates.total_referrals, color: 'text-blue-600' },
    { label: 'Qualified Referrals', value: stats.affiliates.qualified_referrals, color: 'text-indigo-600' },
    { label: 'Total Commissions Earned', value: `$${(stats.affiliates.total_commission_earned_cents / 100).toFixed(2)}`, color: 'text-green-600' },
    { label: 'Pending Payouts', value: `$${(stats.affiliates.pending_payout_cents / 100).toFixed(2)}`, color: 'text-orange-600' },
    { label: 'Business Referrals', value: stats.business_referrals.total, color: 'text-purple-600' },
    { label: 'Rewards Applied', value: stats.business_referrals.rewards_applied, color: 'text-teal-600' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {cards.map((card) => (
        <Card key={card.label}>
          <CardBody>
            <p className="text-sm text-gray-500">{card.label}</p>
            <p className={`text-2xl font-bold ${card.color}`}>{card.value}</p>
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

// ── Applications View ──
function ApplicationsView({
  data,
  loading,
  page,
  setPage,
  statusFilter,
  setStatusFilter,
  onReview,
}: {
  data?: { applications: AffiliateApplication[]; total: number };
  loading: boolean;
  page: number;
  setPage: (p: number) => void;
  statusFilter: string;
  setStatusFilter: (s: string) => void;
  onReview: (app: AffiliateApplication) => void;
}) {
  const statusBadge = (status: string) => {
    const variants: Record<string, string> = {
      pending: 'warning',
      approved: 'success',
      rejected: 'danger',
    };
    return <Badge variant={(variants[status] || 'secondary') as any}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Applications</h2>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="rounded-md border-gray-300 text-sm"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="text-gray-500 py-8 text-center">Loading...</div>
        ) : !data?.applications.length ? (
          <div className="text-gray-500 py-8 text-center">No applications found</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organization</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.applications.map((app) => (
                    <tr key={app.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{app.full_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{app.email}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{app.organization || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{app.organization_type || '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(app.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">{statusBadge(app.status)}</td>
                      <td className="px-4 py-3 text-sm">
                        {app.status === 'pending' && (
                          <Button size="sm" onClick={() => onReview(app)}>Review</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} setPage={setPage} total={data.total} pageSize={20} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ── Partners View ──
function PartnersView({
  data,
  loading,
  page,
  setPage,
  onToggle,
  onViewDetail,
}: {
  data?: { partners: AffiliatePartner[]; total: number };
  loading: boolean;
  page: number;
  setPage: (p: number) => void;
  onToggle: (id: string) => void;
  onViewDetail: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold">Partners</h2>
      </CardHeader>
      <CardBody>
        {loading ? (
          <div className="text-gray-500 py-8 text-center">Loading...</div>
        ) : !data?.partners.length ? (
          <div className="text-gray-500 py-8 text-center">No partners yet</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Referrals</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Earned</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data.partners.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">
                        <div className="font-medium text-gray-900">{p.full_name}</div>
                        <div className="text-xs text-gray-500">{p.organization || p.email}</div>
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-blue-600">
                        {p.promo_codes?.code || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {p.total_qualified_referrals}/{p.total_referrals}
                      </td>
                      <td className="px-4 py-3 text-sm text-green-600 font-medium">
                        ${(p.total_commission_earned_cents / 100).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-orange-600">
                        ${(p.pending_payout_cents / 100).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <Badge variant={p.is_active ? 'success' : 'danger'}>
                          {p.is_active ? 'Active' : 'Suspended'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex gap-2">
                          <Button size="sm" variant="secondary" onClick={() => onViewDetail(p.id)}>
                            View
                          </Button>
                          <Button
                            size="sm"
                            variant={p.is_active ? 'danger' : 'secondary'}
                            onClick={() => onToggle(p.id)}
                          >
                            {p.is_active ? 'Suspend' : 'Reactivate'}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={page} setPage={setPage} total={data.total} pageSize={20} />
          </>
        )}
      </CardBody>
    </Card>
  );
}

// ── Pagination ──
function Pagination({
  page,
  setPage,
  total,
  pageSize,
}: {
  page: number;
  setPage: (p: number) => void;
  total: number;
  pageSize: number;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between mt-4 px-4">
      <span className="text-sm text-gray-500">
        Page {page} of {totalPages} ({total} total)
      </span>
      <div className="flex gap-2">
        <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          Previous
        </Button>
        <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}
